import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
  featureFlagsSelector,
  arrivalDataSelector,
  housingDataSelector,
  nextStepSelector,
  repApprovedSelector,
  repEditableSelector,
  residentialHistoryDataSelector,
  selectIsOnboardingDataUpdated,
  setOnboardingFormCompleted,
  updateRepAsync,
  vehicleDataSelector,
} from '@/redux/onboarding';
import { ArrivalStatus, HousingStatus, ResidentialHistory, VehicleStatus } from '../../../recruits/components/HousingInfo';
import { isAdminSelector } from '@/redux/auth';
import { formLoadingSelector } from '@/redux/loading';
import { updateRepByIdAsync } from '@/redux/reps';
import { onboardingConstants } from '@/lib';
import { ProfileWizardContext, WizardFormWrapper } from '../../components';
import { CustomButtonGroup, FormSection, toastType } from '@/components/common';
import { composeValidationSchema } from './housingAndVhValidationSchema';
import { useProfileEditable } from '../../components/ProfileWizard/hooks';
import { useScrollIntoView, useStableCallback } from '@/hooks';
import { adapterMapModified } from '@/lib/adapters';
import { resetOnboardingSectionData } from '@/lib/initialState';
import { addToastsAction } from '@/redux/toasts';

const {
  HOUSING_AND_VEHICLES_FORM_NAME,
  HOUSING_AND_VEHICLES_FORM_TITLE,
  HOUSING_SECTION_NAME,
  HOUSING_SECTION_TITLE,
  HOUSING_SECTION_FEATURE_FLAG_NAME,
  RESIDENTIAL_HISTORY_SECTION_NAME,
  RESIDENTIAL_HISTORY_SECTION_TITLE,
  RESIDENTIAL_HISTORY_SECTION_FEATURE_FLAG_NAME,
  VEHICLES_SECTION_NAME,
  VEHICLES_SECTION_TITLE,
  VEHICLES_SECTION_FEATURE_FLAG_NAME,
  WIZARD_TYPE_RECRUIT,
  VALIDATION_ERROR_MESSAGE_FOR_TOAST,
} = onboardingConstants;

const HousingAndVehicleForm = ({
  // Own Props
  wizardType = WIZARD_TYPE_RECRUIT,
  isPersonalWizard = true,
  userId,
  cancelToken,

  // State / Dispatch
  isUpdated,
  isRepEditable,
  isApproved,
  isAdmin,
  featureFlags,
  formLoading,
  arrivalData,
  housingData,
  residentialHistory,
  vehicleData,
  nextStep,
  updateRep,
  updateRepById,
  setFormCompleted,
  addToasts,
}) => {
  const {
    [HOUSING_SECTION_FEATURE_FLAG_NAME]: isHousingSectionEnabled,
    [RESIDENTIAL_HISTORY_SECTION_FEATURE_FLAG_NAME]: isResidentialHistorySectionEnabled,
    [VEHICLES_SECTION_FEATURE_FLAG_NAME]: isVehiclesSectionEnabled,
  } = featureFlags;

  /**
   * If no one of the sections is enabled, we don't need to render the form
   * as well as don't submit it upon processing nextStep transition.
   *
   * @type {boolean}
   */
  const hasEnabledSections = Object.values({
    isHousingSectionEnabled,
    isResidentialHistorySectionEnabled,
    isVehiclesSectionEnabled,
  }).some((value) => value === true);

  const [shouldScroll, setShouldScroll] = useState(false);

  const { section, setSection, showViewHistory, recruitingSeasonId } = useContext(ProfileWizardContext);

  const formRef = useRef(null);
  const housingSectionRef = useRef(null);
  const residentialHistorySectionRef = useRef(null);
  const vehiclesSectionRef = useRef(null);

  const methods = useForm({
    defaultValues: {
      ...arrivalData,
      ...housingData,
      ...residentialHistory,
      ...vehicleData,
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(composeValidationSchema(featureFlags)),
    context: { isAdmin, isWizard: isPersonalWizard }, // TODO: rename isWizard to isPersonalWizard in validation schema
  });

  const {
    handleSubmit,
    formState: {
      isDirty,
      isSubmitted,
      errors,
    },
    reset,
    setValue,
    getValues,
    clearErrors,
  } = methods;

  // When evaluating form completion, emulate the "Personal Wizard" context for correct results.
  const isCompleted = composeValidationSchema(featureFlags).isValidSync(getValues(), {
    context: {
      isAdmin: false,
      isWizard: true,
    },
  });

  const { isEditable, canEditField } = useProfileEditable({
    wizardType,
    isPersonalWizard,
    isAdmin,
    isApproved,
    isRepEditable,
  });

  const handleChange = useCallback((event) => {
    const { name, type, checked } = event.target;
    let { value } = event.target;

    value = type === 'checkbox' ? checked : value;

    setValue(name, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });

    resetOnboardingSectionData(name, value, (name, value) => {
      setValue(name, value, { shouldDirty: true }); // Skip validation, but recompute the `isDirty` when resetting sub-fields.
      clearErrors(name); // Clear errors for sub-fields if any.
    });
  }, [
    setValue,
    clearErrors,
  ]);

  const onBlurTrimSpace = useCallback((event, name) => {
    handleChange({
      target: {
        name,
        value: event.target.value.trim(),
      },
      type: event.type,
    });
  }, [handleChange]);

  const handleSubmitForm = useStableCallback((onSuccess) => {
    const updateRepCallback = userId ? updateRepById : updateRep;
    const sendingData = adapterMapModified['combined'](getValues());

    updateRepCallback({
      data: {
        ...sendingData,
        ...(userId && { userId }),
        ...(recruitingSeasonId && { recruitingSeasonId: recruitingSeasonId }),
      },
      ...(cancelToken && { cancelToken }),
      successCallback: onSuccess ?? (() => {}),
    });
  });

  const handleResetForm = useCallback(() => {
    reset({
      ...arrivalData,
      ...housingData,
      ...residentialHistory,
      ...vehicleData,
    });
  }, [
    reset,
    arrivalData,
    housingData,
    residentialHistory,
    vehicleData,
  ]);

  useEffect(() => {
    setFormCompleted({ formId: HOUSING_AND_VEHICLES_FORM_NAME, isCompleted });
  }, [isCompleted, setFormCompleted]);

  useEffect(() => {
    if (isUpdated) {
      handleResetForm();
    }
    // we don't want to reset the form when handleResetForm changes as we might lose data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdated]);

  useEffect(() => {
    if (nextStep) {
      if (hasEnabledSections && isEditable) {
        handleSubmit(
          () => handleSubmitForm(nextStep.applyTransition),
          () => nextStep.declineTransition(),
        )();
      } else {
        nextStep.applyTransition();
      }
    }
  }, [
    nextStep,
    hasEnabledSections,
    isEditable,
    handleSubmit,
    handleSubmitForm,
  ]);

  /**
   * Initially `formLoading` is undefined which interprets as false
   * and React mounts a form component.
   *
   * However, in a moment `formLoading` becomes true indicating the real data load.
   * React unmounts the form component and mount Loader instead.
   *
   * And only when data is loaded completely and `formLoading becomes undefined/false again
   * we're ready to scroll to desired form section.
   */
  useEffect(() => {
    if (!shouldScroll && !formLoading) {
      setShouldScroll(true);
    }
  }, [shouldScroll, formLoading]);

  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length > 0) {
      addToasts([{
        type: toastType.ERROR,
        message: VALIDATION_ERROR_MESSAGE_FOR_TOAST,
      }]);
    }
  }, [isSubmitted, errors, addToasts]);

  useScrollIntoView(
    shouldScroll && {
      [HOUSING_SECTION_NAME]: housingSectionRef,
      [RESIDENTIAL_HISTORY_SECTION_NAME]: residentialHistorySectionRef,
      [VEHICLES_SECTION_NAME]: vehiclesSectionRef,
    }[section],
    (parent) => !(parent === formRef.current.parentElement),
    () => setSection(null),
  );

  return hasEnabledSections && (
    <WizardFormWrapper ref={formRef} title={HOUSING_AND_VEHICLES_FORM_TITLE} isLoading={formLoading}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(() => handleSubmitForm())}>
          {isHousingSectionEnabled && (
            <FormSection
              ref={housingSectionRef}
              title={HOUSING_SECTION_TITLE}
              viewHistory={isAdmin && !isPersonalWizard}
              onViewHistory={() => {
                showViewHistory(HOUSING_SECTION_NAME);
              }}
            >
              <ArrivalStatus
                isAdmin={isAdmin}
                formLoading={formLoading}
                canEditField={canEditField}
                onChangeHandler={handleChange}
              />
              <HousingStatus
                canEditField={canEditField}
                onChangeHandler={handleChange}
              />
            </FormSection>
          )}

          {isResidentialHistorySectionEnabled && (
            <FormSection
              ref={residentialHistorySectionRef}
              title={RESIDENTIAL_HISTORY_SECTION_TITLE}
              viewHistory={isAdmin && !isPersonalWizard}
              onViewHistory={() => {
                showViewHistory(RESIDENTIAL_HISTORY_SECTION_NAME);
              }}
            >
              <ResidentialHistory
                canEditField={canEditField}
                onChangeHandler={handleChange}
                onBlurTrimSpace={onBlurTrimSpace}
              />
            </FormSection>
          )}

          {isVehiclesSectionEnabled && (
            <FormSection
              ref={vehiclesSectionRef}
              title={VEHICLES_SECTION_TITLE}
              viewHistory={isAdmin && !isPersonalWizard}
              onViewHistory={() => {
                showViewHistory(VEHICLES_SECTION_NAME);
              }}
            >
              <VehicleStatus
                canEditField={canEditField}
                onChangeHandler={handleChange}
                onBlurTrimSpace={onBlurTrimSpace}
              />
            </FormSection>
          )}

          {isEditable && (
            <CustomButtonGroup
              orientation="right"
              onCancelClick={handleResetForm}
              disabledCancel={!isDirty}
              withSubmit
            />
          )}
        </form>
      </FormProvider>
    </WizardFormWrapper>
  );
};

HousingAndVehicleForm.propTypes = {
  wizardType: PropTypes.string,
  isPersonalWizard: PropTypes.bool,
  userId: PropTypes.number,
  isUpdated: PropTypes.bool,
  isApproved: PropTypes.bool,
  isRepEditable: PropTypes.bool,
  isAdmin: PropTypes.bool,
  featureFlags: PropTypes.object,
  formLoading: PropTypes.bool,
  arrivalData: PropTypes.object,
  housingData: PropTypes.object,
  residentialHistory: PropTypes.object,
  vehicleData: PropTypes.object,
  updateRep: PropTypes.func,
  updateRepById: PropTypes.func,
  setFormCompleted: PropTypes.func,
  nextStep: PropTypes.shape({
    applyTransition: PropTypes.func.isRequired,
    declineTransition: PropTypes.func.isRequired,
  }),
  cancelToken: PropTypes.object,
  addToasts: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isUpdated: selectIsOnboardingDataUpdated(state),
  isRepEditable: repEditableSelector(state),
  isApproved: repApprovedSelector(state),
  isAdmin: isAdminSelector(state),
  featureFlags: featureFlagsSelector(state),
  arrivalData: arrivalDataSelector(state),
  housingData: housingDataSelector(state),
  residentialHistory: residentialHistoryDataSelector(state),
  vehicleData: vehicleDataSelector(state),
  formLoading: formLoadingSelector(state),
  nextStep: nextStepSelector(state),
});

const mapDispatchToProps = {
  updateRep: updateRepAsync.request,
  updateRepById: updateRepByIdAsync.request,
  setFormCompleted: setOnboardingFormCompleted,
  addToasts: addToastsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(HousingAndVehicleForm);
